import axios from "axios";
import Cookies from 'js-cookie';
import { io } from 'socket.io-client';
import { getLanguage } from './i18n';

export const getUserTimeZone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
        return 'UTC';
    }
};

const NODE_ENV = (process.env.NODE_ENV || '').trim().toLowerCase();
const IS_DEVELOPMENT = NODE_ENV === 'development';
const baseURL = process.env.REACT_APP_API_URL || (IS_DEVELOPMENT ? "http://localhost:3001" : "https://app.labia.pro/api");
const socketURL = baseURL.replace('/api', '');
const socket = io(socketURL, {
    autoConnect: false,
    withCredentials: true,
    path: IS_DEVELOPMENT ? "/socket" : "/api/socket"
  });
const ERROR = {
    PIPELINE: 'PIPELINE',
    PIPELINE_STAGES: 'PIPELINE_STAGES',
    COMPANY: 'COMPANY',
    CONTACT: 'CONTACT',
    TASK: 'TASK',
    MEETING: 'MEETING',
    DEAL: 'DEAL',
    MAIL: 'MAIL',
    NOTE: 'NOTE',
    CALL: 'CALL',
    ASSOCIATIONS: 'ASSOCIATIONS',
    UNKNOWN: 'UNKNOWN'
}

//axios.defaults.headers.common['sessionId'] = Cookies.get('token');
axios.defaults.withCredentials = true;

function CreateDealFromJson(json) {
    return new Promise((resolve, reject) => {
        var data = JSON.parse(JSON.stringify(json));
        // Clean up the data object
        if (data.deal) {
            if (data.deal.pipeline) {
                // Keep only id and stage info
                data.deal.pipeline = {
                    id: data.deal.pipeline.id,
                    stage: data.deal.pipeline.stage ? {
                        id: data.deal.pipeline.stage.id
                    } : undefined
                };
            }
        }

        // Clean up note associations
        if (data.note) {
            if (data.note.contacts) {
                data.note.contacts = data.note.contacts.map(contact => ({
                    id: contact.id,
                    firstName: contact.firstName,
                    lastName: contact.lastName
                }));
            }
            if (data.note.companies) {
                data.note.companies = data.note.companies.map(company => ({
                    id: company.id,
                    name: company.name
                }));
            }
            if (data.note.deals) {
                data.note.deals = data.note.deals.map(deal => ({
                    id: deal.id
                }));
            }
        }

        // Clean up meeting attendees
        if (data.meeting && data.meeting.attendees) {
            data.meeting.attendees = data.meeting.attendees.map(attendee => ({
                id: attendee.id,
                email: attendee.email,
                firstName: attendee.firstName,
                lastName: attendee.lastName
            }));
        }

        // Clean up mail recipients 
        if (data.mail && data.mail.recipient) {
            data.mail.recipient = data.mail.recipient.map(recipient => ({
                id: recipient.id,
                firstName: recipient.firstName,
                lastName: recipient.lastName,
                email: recipient.email // Keep email for sending
            }));
        }

        // Strip association objects to {id} refs
        if (data.deal?.contact) data.deal.contact = { id: data.deal.contact.id };
        if (data.deal?.company) data.deal.company = { id: data.deal.company.id };
        if (data.task?.contact) data.task.contact = { id: data.task.contact.id };
        if (data.task?.companies) data.task.companies = data.task.companies.filter(c => c?.id).map(c => ({ id: c.id }));
        if (data.task?.deals) data.task.deals = data.task.deals.filter(d => d?.id).map(d => ({ id: d.id }));
        if (data.call?.contact) data.call.contact = { id: data.call.contact.id };
        if (data.call?.companies) data.call.companies = data.call.companies.filter(c => c?.id).map(c => ({ id: c.id }));
        if (data.call?.deals) data.call.deals = data.call.deals.filter(d => d?.id).map(d => ({ id: d.id }));
        if (data.meeting?.companies) data.meeting.companies = data.meeting.companies.filter(c => c?.id).map(c => ({ id: c.id }));
        if (data.meeting?.deals) data.meeting.deals = data.meeting.deals.filter(d => d?.id).map(d => ({ id: d.id }));
        if (data.mail?.companies) data.mail.companies = data.mail.companies.filter(c => c?.id).map(c => ({ id: c.id }));
        if (data.mail?.deals) data.mail.deals = data.mail.deals.filter(d => d?.id).map(d => ({ id: d.id }));

        // Remove any options arrays
        Object.keys(data).forEach(key => {
            if (data[key]) {
                delete data[key].options;
                delete data[key].companiesOptions;
                delete data[key].contactsOptions; 
                delete data[key].dealsOptions;
            }
        });

        console.log(JSON.stringify(data));
        axios.post(`${baseURL}/createDeal`, data, { headers: { 'language': getLanguage() } })
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                console.log(response.response);
                if (response.code == "ERR_BAD_REQUEST" && response.status != 401) {
                    processError(response.response.data, reject);
                } else {
                    reject(response.response)
                }
            });
    });
}


function CreateJSONFromText(text, updateProgress, resolve, reject) {
    if (socket.connected) {
        socket.emit('text', { data: { text: text } });
    } else {
        socket.connect();
        socket.on('connect', () => {
            console.log('connected');
            socket.emit('text', { data: { text: text } });
        });
    }
    socket.on('disconnect', () => {
        console.log('disconnected');
    });
    socket.on('progress', (progress) => {
        console.log(progress);
        updateProgress(progress);
    });
    socket.on('text', (response) => {
        resolve(response);
        socket.disconnect();
        socket.close();
    });
    socket.on('actions', (actions) => {
        
    });

    socket.on('error', (error) => {
        socket.disconnect();
        socket.close();
        console.log(error);
        if (error.code == "ERR_BAD_REQUEST" && error.status != 401) {
            processError(error.response.data, reject);
        } else {
            reject(error.response)
        }
    });
}


function UploadAudio(audio, updateProgress, signal) {
    return new Promise((resolve, reject) => {
        var bodyFormData = new FormData();
        bodyFormData.append('audio', audio, `${makeid(5)}.webm`);
        bodyFormData.append('language', getLanguage());
        bodyFormData.append('timezone', getUserTimeZone());
        axios({
            method: "post",
            url: `${baseURL}/audio`,
            data: bodyFormData,
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: updateProgress,
            signal: signal
          })
            .then(function (response) {
                console.log(response);
                resolve(response.data);
            })
            .catch(function (response) {
                if (axios.isCancel(response)) {
                    reject({ cancelled: true });
                    return;
                }
                console.log(response.response);
                if (response.code == "ERR_BAD_REQUEST" && response.response.status != 401) {
                    processError(response.response.data, reject);
                } else {
                    reject(response.response)
                }
            });
    });
}

function makeid(length) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
}

function processError(error, reject) {
    if (error.message) {
        let errorType = error.message.toUpperCase()
        let originalJson = error.originalJson;
        switch (errorType) {
            case 'PIPELINE':
                reject({error: ERROR.PIPELINE, originalJson: originalJson, options: error.options})
                break;
            case 'COMPANY':
                reject({error: ERROR.COMPANY, originalJson: originalJson, options: error.options})
                break;
            case 'CONTACT':
                reject({error: ERROR.CONTACT, originalJson: originalJson, options: error.options})
                break;
            case 'TASK':
                reject({error: ERROR.TASK, originalJson: originalJson, options: error.options})
                break;
            case 'MEETING':
                reject({error: ERROR.MEETING, originalJson: originalJson, options: error.options})
                break;
            case 'DEAL':
                reject({error: ERROR.DEAL, originalJson: originalJson, options: error.options})
                break;
            case 'EMAIL':
                reject({error: ERROR.MAIL, originalJson: originalJson, options: error.options})
                break;
            case 'NOTE':
                reject({error: ERROR.NOTE, originalJson: originalJson, options: error.options})
                break;
            case 'CALL':
                reject({error: ERROR.CALL, originalJson: originalJson, options: error.options})
                break;
            default:
                reject({error: ERROR.UNKNOWN, originalJson: originalJson, options: error.options})
        }
    }
}

function fetchCompanies({
    value,
    after,
    createdate,
    noCache,
    signal
}) {
    return new Promise((resolve, reject) => {
        var url = `${baseURL}/companies?`;
        if (value) {
            url += `companyName=${value}`;
        }
        if (noCache) {
            url += `&noCache=${noCache}`;
        }
        if (after) {
            url += `&after=${after}`;
        }
        if (createdate) {
            url += `&createdate=${createdate}`;
        }
        axios.get(url, { signal: signal })
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function fetchOwners() {
    return axios.get(`${baseURL}/owners`)
        .then(r => r.data)
        .catch(() => []);
}

function fetchDeals(value, after, signal) {
    return new Promise((resolve, reject) => {
        var url = `${baseURL}/deals`;
        if (value) {
            url += `?deal=${value}`;
        }
        if (after) {
            url += `&after=${after}`;
        }
        axios.get(url, { signal: signal })
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function fetchDeal(id) {
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}/deal/${id}`)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function fetchCompany(id) {
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}/company/${id}`)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function fetchDealsGrouped({value, after, closedate, noCache, signal}) {
    return new Promise((resolve, reject) => {
        var url = `${baseURL}/dealsGrouped?`;
        if (value) {
            url += `deal=${value}`;
        }
        if (after) {
            url += `&after=${after}`;
        }
        if (closedate) {
            url += `&closedate=${closedate}`;
        }
        if (noCache) {
            url += `&noCache=${noCache}`;
        }
        axios.get(url, { signal: signal })
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}


function fetchContacts({firstName, after, createdate, noCache, signal}) {
    return new Promise((resolve, reject) => {
        var url = `${baseURL}/contacts?`;
        if (firstName) {
            url += `firstName=${firstName}`;
        }
        if (after) {
            url += `&after=${after}`;
        }
        if (createdate) {
            url += `&createdate=${createdate}`;
        }
        if (noCache) {
            url += `&noCache=${noCache}`;
        }
        axios.get(url, {signal: signal})
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function fetchContact(id) {
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}/contact/${id}`)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function searchContacts(firstName, page, signal) {
    return new Promise((resolve, reject) => {
        var url = `${baseURL}/searchcontacts`;
        if (firstName) {
            url += `?firstName=${firstName}`;
        }
        if (page) {
            url += `&page=${page}`;
        }
        axios.get(url, {signal: signal})
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}


function getUserData() {
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}/user`)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function fetchPipelines() {
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}/pipelines`)
            .then(function (response) {
                // Normalize Redis-cached format { id, value: { label, stages } } to flat { id, label, stages }
                const data = Array.isArray(response.data) ? response.data : [];
                const normalized = data.map(pl => {
                    if (pl.value && typeof pl.value === 'object') {
                        // Use inner value.id (e.g. "default") not outer Redis id ("domain:pipeline:default")
                        // — inner id matches what HubSpot stores on deal.pipeline field, and has no dots
                        return {
                            id: pl.value.id || pl.id,
                            label: (pl.value.label || pl.id || '').trim(),
                            stages: (Array.isArray(pl.value.stages) ? pl.value.stages : []).map(st => ({ id: st.id, label: st.label || st.id })),
                        };
                    }
                    return pl;
                });
                resolve(normalized);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function logout() {
    return new Promise((resolve, reject) => {
        axios.post(`${baseURL}/logout`)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function createContact(data) {
    return new Promise((resolve, reject) => {
        axios.post(`${baseURL}/contacts`, data)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function createCompany(data) {
    return new Promise((resolve, reject) => {
        axios.post(`${baseURL}/companies`, data)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function disconnectGmail() {
    return new Promise((resolve, reject) => {
        axios.post(`${baseURL}/gmail/disconnect`)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function fetchProperties(objectType, force = false, all = false, pipelineId = null, stageId = null) {
    return new Promise((resolve, reject) => {
        let url = `${baseURL}/properties/${objectType}?force=${force}&all=${all}`;
        if (pipelineId) url += `&pipelineId=${encodeURIComponent(pipelineId)}`;
        if (stageId) url += `&stageId=${encodeURIComponent(stageId)}`;
        axios.get(url)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function fetchDomainProperties() {
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}/domain-properties`)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function updateProperties(objectType, properties) {
    return new Promise((resolve, reject) => {
        axios.post(`${baseURL}/properties/${objectType}`, properties)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function updateStageProperties(pipelineId, stageId, properties) {
    return new Promise((resolve, reject) => {
        axios.post(`${baseURL}/properties/deal/stages/${pipelineId}/${stageId}`, { properties })
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function fetchStageProperties(pipelineId, stageId) {
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}/domain-properties/stages/${pipelineId}/${stageId}`)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function fetchTasks(query, createdate = "ASCENDING", status, signal) {
    return new Promise((resolve, reject) => {
        let url = `${baseURL}/tasks?query=${query}&createdate=${createdate}`;
        if (status) url += `&status=${status}`;
        axios.get(url, {signal: signal})
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function fetchTask(id) {
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}/task/${id}`)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function updateTask(id, task) {
    return new Promise((resolve, reject) => {
        axios.post(`${baseURL}/task/${id}`, task)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function getAudioHistory() {
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}/audio`)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function updateAudioText(id, text) {
    return new Promise((resolve, reject) => {
        axios.put(`${baseURL}/audio/${id}`, { text: text })
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function uploadPhoto(imageData, text) {
    return new Promise((resolve, reject) => {
        var bodyFormData = new FormData();
        bodyFormData.append('image', imageData, `${makeid(5)}.jpg`);
        bodyFormData.append('language', getLanguage());
        if (text) {
            bodyFormData.append('text', text);
        }
        axios.post(`${baseURL}/ocr/upload`, bodyFormData, { 
            headers: { 
                'language': getLanguage(),
                'Content-Type': "multipart/form-data"
            }
        })
        .then(function (response) {
            resolve(response.data);
        })
        .catch(function (error) {
            processError(error, reject);
        });
    });
}

function uploadEmailAttachments(files, signal, onUploadProgress) {
    return new Promise((resolve, reject) => {
        const bodyFormData = new FormData();
        (files || []).forEach((file) => {
            if (file) bodyFormData.append('attachments', file, file.name || `${makeid(5)}.bin`);
        });
        axios.post(`${baseURL}/email/attachments/upload`, bodyFormData, {
            headers: { 'Content-Type': "multipart/form-data" },
            signal,
            onUploadProgress,
        })
            .then(function (response) {
                resolve(response.data?.attachments || []);
            })
            .catch(function (error) {
                reject(error?.response || error);
            });
    });
}

function fetchRecentEmailAttachments(limit = 12, signal) {
    return new Promise((resolve, reject) => {
        const safeLimit = Math.min(Math.max(Number(limit) || 12, 1), 50);
        axios.get(`${baseURL}/email/attachments/recent?limit=${safeLimit}`, { signal })
            .then(function (response) {
                resolve(response.data?.attachments || []);
            })
            .catch(function (error) {
                reject(error?.response || error);
            });
    });
}

function RewriteText(text, instruction, language) {
    return new Promise((resolve, reject) => {
        axios.post(`${baseURL}/ai/rewrite`, { text, instruction, language })
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response)
            });
    });
}

function sendPropertyChatMessage(message, language) {
    if (!socket.connected) socket.connect();
    socket.emit('propertyChat', { message, language });
}

function applyPropertyChatConfig(actions) {
    if (!socket.connected) socket.connect();
    socket.emit('propertyChatApply', { actions });
}

function getCompanyProfile() {
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}/company-profile`)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response);
            });
    });
}

function getWritingStyle() {
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}/writing-style`)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response);
            });
    });
}

function getPropertyChatIntro(language = getLanguage()) {
    return new Promise((resolve, reject) => {
        axios.get(`${baseURL}/property-chat/intro?language=${encodeURIComponent(language)}`)
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (response) {
                reject(response);
            });
    });
}

function SubmitAudioCorrections(corrections) {
    return new Promise((resolve) => {
        if (!Array.isArray(corrections) || corrections.length === 0) {
            resolve({ inserted: 0 });
            return;
        }
        axios.post(`${baseURL}/audioCorrections`, { corrections })
            .then((response) => resolve(response.data))
            .catch((error) => {
                try {
                    if (window.Sentry?.captureException) window.Sentry.captureException(error);
                } catch (_e) { /* silent */ }
                resolve({ inserted: 0 });
            });
    });
}

function SubmitCRMObjects(objects, options = {}) {
    return new Promise((resolve, reject) => {
        const headers = { 'language': getLanguage() };
        if (options.flowId) headers['x-flow-id'] = options.flowId;
        axios.post(`${baseURL}/submitObjects`, objects, { headers })
            .then(function (response) {
                resolve(response.data);
            })
            .catch(function (error) {
                if (error?.code === "ERR_BAD_REQUEST" && error?.response?.status !== 401) {
                    processError(error.response.data, reject);
                } else {
                    reject(error?.response || error);
                }
            });
    });
}

export default {
    baseURL,
    ERROR,
    CreateDealFromJson,
    CreateJSONFromText,
    UploadAudio,
    fetchCompanies,
    fetchContact,
    fetchContacts,
    fetchPipelines,
    searchContacts,
    getUserData,
    createCompany,
    createContact,
    fetchDeals,
    logout,
    processError,
    fetchDealsGrouped,
    fetchDeal,
    fetchCompany,
    disconnectGmail,
    fetchProperties,
    fetchDomainProperties,
    socket,
    updateProperties,
    updateStageProperties,
    fetchStageProperties,
    fetchTasks,
    fetchTask,
    updateTask,
    getAudioHistory,
    updateAudioText,
    uploadPhoto,
    uploadEmailAttachments,
    fetchRecentEmailAttachments,
    RewriteText,
    sendPropertyChatMessage,
    applyPropertyChatConfig,
    getCompanyProfile,
    getWritingStyle,
    getPropertyChatIntro,
    SubmitCRMObjects,
    SubmitAudioCorrections,
    fetchOwners
}